import { RBAdminEmail } from "../../../util/Common";
import { isRBPayAvailableForLocation, isRBPayPaymentMethod } from "../../../util/rbPay";
import { RBPayService } from "../../../api/RBPayService";
import { rbPayrollOwnerMockApi } from "./rbPayrollOwner.mockApi";
import { maskAccountLast4 } from "../utils/rbPayrollOwner.formatters";
import {
  deserializeLocationProfile,
  resolveOwnerProfileStatus,
  serializeLocationProfile,
} from "../utils/rbPayrollOwner.mappers";
import { RB_PAYROLL_PAYMENT_OPTION_CONFIG } from "../constants/rbPayrollOwner.constants";
import {
  RBPayrollDocument,
  RBPayrollDuplicateSource,
  RBPayrollPaymentMethod,
  RBPayrollPaymentOption,
  RBPayrollSetupState,
  RBPayrollStatus,
} from "../types/rbPayrollOwner.types";

// A location with no RB Pay profile yet returns 404 — treated as Not Started
// rather than an error so the setup flow (and duplicate-source scan) can proceed.
const isNotFoundError = (error: any) => error?.response?.status === 404;

// Surface the backend message when present; otherwise a user-safe fallback.
const resolveApiErrorMessage = (error: any, fallback: string) =>
  (error && error.response && error.response.data && error.response.data.message) || fallback;

// BE has authorized the owner/location roles on `rb-pay/worker-profile/void-cheque`
// (previously 403 "Forbidden resource", gated to worker/admin). The real upload is
// now enabled. Kept as a toggle: flip back to `true` only if the endpoint regresses
// for owners, which skips the upload and uses a placeholder S3 key.
const BYPASS_VOID_CHEQUE_UPLOAD = false;
const BYPASS_VOID_CHEQUE_S3_KEY = "uploads/void-cheque.pdf";

interface BuildDefaultSetupArgs {
  account: any;
  location: any;
}

interface GetPaymentOptionsArgs {
  account: any;
  location: any;
}

interface SavePaymentOptionArgs {
  account: any;
  location: any;
  paymentMethod: string;
}

interface GetSetupArgs {
  account: any;
  location: any;
}

interface SaveSetupArgs {
  account: any;
  location: any;
  setup: RBPayrollSetupState;
}

interface DuplicateSetupArgs {
  account: any;
  fromLocationId: string;
  toLocation: any;
}

const resolveLocationId = (location: any) => String(location?.id || "");

// Resolve the location's network (fall back to the owner's first network) so the
// RB Pay config can be read from the live BE payload: `tempwisePortalUrl` (View
// Statements destination) and `rbPayContactEmail` (RB Pay support address).
const resolveNetwork = (account: any, location: any) => {
  const networks = Array.isArray(account?.networks) ? account.networks : [];
  const byLocation = location?.networkId
    ? networks.find((network: any) => String(network?.id) === String(location.networkId))
    : null;
  return byLocation || networks[0] || null;
};
const resolveContactEmail = (account: any, location?: any) => {
  const network = resolveNetwork(account, location);
  return String(
    network?.rbPayContactEmail ||
      network?.contactEmail ||
      localStorage.getItem("contactEmail") ||
      RBAdminEmail
  );
};
const resolveNetworkConfiguration = (account: any, location: any) => {
  const network = resolveNetwork(account, location);
  return {
    statementPortalUrl: String(network?.tempwisePortalUrl || ""),
    contactEmail: resolveContactEmail(account, location),
  };
};

const normalizeStatus = (value?: string | null) => {
  const normalized = String(value || "").trim().toUpperCase();

  if (normalized === RBPayrollStatus.ACTIVE) {
    return RBPayrollStatus.ACTIVE;
  }

  if (normalized === RBPayrollStatus.PENDING_REVIEW) {
    return RBPayrollStatus.PENDING_REVIEW;
  }

  if (normalized === RBPayrollStatus.ON_HOLD) {
    return RBPayrollStatus.ON_HOLD;
  }

  if (normalized === RBPayrollStatus.REJECTED) {
    return RBPayrollStatus.REJECTED;
  }

  return RBPayrollStatus.NOT_STARTED;
};

const buildDefaultSetup = async ({ account, location }: BuildDefaultSetupArgs): Promise<RBPayrollSetupState> => {
  const networkConfiguration = resolveNetworkConfiguration(account, location);

  return {
    locationId: resolveLocationId(location),
    locationName: String(location?.name || ""),
    directDeposit: {
      institutionNumber: "",
      transitNumber: "",
      accountNumber: "",
      document: null,
      authorizationAccepted: false,
    },
    approval: {
      paymentMethod: RBPayrollPaymentMethod.RB_PAYROLL,
      status: RBPayrollStatus.NOT_STARTED,
      approved: false,
      submittedAt: "",
      approvedAt: "",
      rejectedAt: "",
      rejectionReason: "",
    },
    statementPortalUrl: networkConfiguration.statementPortalUrl,
    contactEmail: networkConfiguration.contactEmail,
    meta: {
      updatedAt: null,
      source: "fe-mock",
    },
  };
};

export const rbPayrollOwnerService = {
  async getPaymentOptions({ account, location }: GetPaymentOptionsArgs): Promise<RBPayrollPaymentOption[]> {
    // RB Pay is offered to a location only when its per-location admin flag
    // (pharmacy.isRbPayAvailable) is on. The network flag is reference-only. A
    // location already on RB Pay (paymentType "rb_pay") is exempt so an active
    // setup is never stranded if the flag is later turned off. Returning [] here
    // hides RB Pay from every owner surface that builds its list from getPaymentOptions.
    const rbPayAvailableForLocation = isRBPayAvailableForLocation(location);
    const locationOnRBPay = isRBPayPaymentMethod(location?.paymentType);
    if (!rbPayAvailableForLocation && !locationOnRBPay) {
      return [];
    }

    const locationId = resolveLocationId(location);

    // TODO(BE): Fetch payment options
    const [selectedPaymentMethod, setup] = await Promise.all([
      rbPayrollOwnerMockApi.getSelectedPaymentMethod(locationId),
      this.getRBPayrollSetup({ account, location }),
    ]);

    return [
      {
        ...RB_PAYROLL_PAYMENT_OPTION_CONFIG,
        setupStatus: setup.approval.status,
        approved: setup.approval.approved,
        selected: selectedPaymentMethod === RBPayrollPaymentMethod.RB_PAYROLL,
      },
    ];
  },

  async savePaymentOption({ location, paymentMethod }: SavePaymentOptionArgs): Promise<string> {
    const locationId = resolveLocationId(location);

    // TODO(BE): Save selected payment option
    return rbPayrollOwnerMockApi.saveSelectedPaymentMethod(locationId, paymentMethod);
  },

  async getRBPayrollSetup({ account, location }: GetSetupArgs): Promise<RBPayrollSetupState> {
    const locationId = resolveLocationId(location);

    // `buildDefaultSetup` carries location identity + network config (statements
    // URL / contact email) that the profile response does not include.
    const base = await buildDefaultSetup({ account, location });

    let profile: any = null;
    try {
      profile = await RBPayService.getLocationProfile(locationId);
    } catch (error) {
      if (!isNotFoundError(error)) {
        throw error;
      }
    }

    return profile ? deserializeLocationProfile(profile, base) : base;
  },

  async saveRBPayrollSetup({ account, location, setup }: SaveSetupArgs): Promise<RBPayrollSetupState> {
    const locationId = resolveLocationId(location);
    const payload = serializeLocationProfile(setup);

    // First submission creates the profile; subsequent edits patch it.
    const isExisting = normalizeStatus(setup.approval?.status) !== RBPayrollStatus.NOT_STARTED;
    const profile = isExisting
      ? await RBPayService.updateLocationProfile(locationId, payload)
      : await RBPayService.createLocationProfile(locationId, payload);

    // TODO(BE): replace with a real payment-option endpoint. Kept in sync via the
    // mock so the payment-method selection card stays consistent.
    await this.savePaymentOption({ account, location, paymentMethod: RBPayrollPaymentMethod.RB_PAYROLL });

    const base = await buildDefaultSetup({ account, location });
    return deserializeLocationProfile(profile, base);
  },

  // Re-select RB Pay as the location's ACTIVE payment method when a setup already
  // exists (e.g. the location previously used RB Pay, switched to another method,
  // and now wants RB Pay back). Re-saves the existing banking profile —
  // serializeLocationProfile sends paymentType "rb_pay" — so the backend restores
  // RB Pay as the active method without the owner re-entering banking. Returns
  // true when re-activated; false when no backend profile exists yet (caller
  // should open the first-time setup form instead).
  async reactivateRBPayrollSetup({ account, location }: GetSetupArgs): Promise<boolean> {
    const setup = await this.getRBPayrollSetup({ account, location });
    if (setup.meta.source !== "be") {
      return false;
    }
    await this.saveRBPayrollSetup({ account, location, setup });
    return true;
  },

  async uploadBankingDocument(_location: any, file: File): Promise<RBPayrollDocument> {
    // Real S3 upload via the void-cheque endpoint (shared with the worker flow).
    // The local object URL drives an immediate preview for this session (on reload
    // the GET returns a presigned URL via buildVoidChequeDocument).
    let s3Key = BYPASS_VOID_CHEQUE_S3_KEY;

    if (!BYPASS_VOID_CHEQUE_UPLOAD) {
      try {
        const response = await RBPayService.uploadVoidCheque(file);
        s3Key = String(response?.voidChequeS3Key || "");
      } catch (error) {
        throw new Error(
          resolveApiErrorMessage(error, "Unable to upload document. Please try again.")
        );
      }
    }

    return {
      id: s3Key || `void-${Date.now()}`,
      name: file.name,
      type: file.type,
      size: file.size,
      previewUrl: URL.createObjectURL(file),
      uploadedAt: new Date().toISOString(),
      source: "s3",
      s3Key,
    };
  },

  async deleteBankingDocument(location: any, _documentId: string): Promise<void> {
    resolveLocationId(location);

    // TODO(BE): Delete Banking Document
    return rbPayrollOwnerMockApi.deleteDocument();
  },

  // Copy another of the owner's locations RB Pay banking setup into this
  // location in one step. Calls the duplicate endpoint and maps the returned
  // banking profile into the setup state used by the screen.
  async duplicateRBPayrollSetup({ account, fromLocationId, toLocation }: DuplicateSetupArgs): Promise<RBPayrollSetupState> {
    const targetLocationId = resolveLocationId(toLocation);
    const profile = await RBPayService.duplicateLocationSetup(targetLocationId, fromLocationId);

    const status = resolveOwnerProfileStatus(profile);
    const approved = status === RBPayrollStatus.ACTIVE;
    const networkConfiguration = resolveNetworkConfiguration(account, toLocation);

    return {
      locationId: targetLocationId,
      locationName: String(toLocation?.name || ""),
      directDeposit: {
        institutionNumber: String(profile?.institutionNumber || ""),
        transitNumber: String(profile?.transitNumber || ""),
        accountNumber: String(profile?.accountNumber || ""),
        document: profile?.voidChequeS3Key
          ? {
              id: String(profile.voidChequeS3Key),
              name: "Void Cheque",
              type: "",
              size: 0,
              previewUrl: "",
              uploadedAt: String(profile?.updatedAt || ""),
              source: "mock-existing",
            }
          : null,
        authorizationAccepted: Boolean(profile?.padAuthorized),
      },
      approval: {
        paymentMethod: RBPayrollPaymentMethod.RB_PAYROLL,
        status,
        approved,
        submittedAt: String(profile?.createdAt || ""),
        approvedAt: approved ? String(profile?.updatedAt || "") : "",
        rejectedAt: "",
        rejectionReason: "",
      },
      statementPortalUrl: networkConfiguration.statementPortalUrl,
      contactEmail: networkConfiguration.contactEmail || resolveContactEmail(account, toLocation),
      meta: {
        updatedAt: String(profile?.updatedAt || new Date().toISOString()),
        source: "be",
      },
    };
  },

  async getRBPayrollStatus({ account, location }: GetSetupArgs) {
    // TODO(BE): Fetch RB Payroll Status
    const setup = await this.getRBPayrollSetup({ account, location });
    return setup.approval.status;
  },

  async getRBPayrollApprovalState({ account, location }: GetSetupArgs) {
    // TODO(BE): Fetch RB Payroll Approval State
    const setup = await this.getRBPayrollSetup({ account, location });
    return setup.approval;
  },

  async getRBPayrollRejectionReason({ account, location }: GetSetupArgs) {
    // TODO(BE): Fetch RB Payroll Rejection Reason
    const setup = await this.getRBPayrollSetup({ account, location });
    return setup.approval.rejectionReason;
  },

  async getRBPayrollStatementsUrl(account: any, location: any): Promise<string> {
    return resolveNetworkConfiguration(account, location).statementPortalUrl;
  },

  async getRBPayContactEmail(account: any, location: any): Promise<string> {
    return resolveNetworkConfiguration(account, location).contactEmail;
  },

  async getDuplicateSources(account: any, currentLocationId: string): Promise<RBPayrollDuplicateSource[]> {
    // TODO(BE): Fetch RB Payroll eligible locations for location creation copy flow
    const locations = Array.isArray(account?.pharmacies) ? account.pharmacies : [];
    const sources = await Promise.all(
      locations
        .filter((location: any) => String(location?.id || "") !== String(currentLocationId || ""))
        .map(async (location: any) => {
          const setup = await this.getRBPayrollSetup({ account, location });
          // Eligibility is driven entirely by real backend data: a location can
          // be used as a duplicate source only if it has an actual RB Pay profile
          // with details to copy. `meta.source === 'be'` means the profile exists
          // on the backend (vs. the local NOT_STARTED default). The previous
          // mock `isSelected` (localStorage) signal is intentionally dropped — it
          // varied per device and surfaced banking-less locations that fail on copy.
          const hasRealProfile = setup.meta.source === "be";
          const hasDocument = Boolean(setup.directDeposit.document);
          const hasBankDetails = Boolean(
            setup.directDeposit.institutionNumber || setup.directDeposit.transitNumber || setup.directDeposit.accountNumber
          );

          if (!hasRealProfile || (!hasBankDetails && !hasDocument)) {
            return null;
          }

          return {
            locationId: String(location?.id || ""),
            locationName: String(location?.name || ""),
            status: setup.approval.status,
            updatedAt: setup.meta.updatedAt,
            // Masked at the source — only the institution and last-4 ever leave.
            institutionNumber: hasBankDetails ? String(setup.directDeposit.institutionNumber || "") : "",
            maskedAccount: hasBankDetails ? maskAccountLast4(setup.directDeposit.accountNumber) : "",
          } as RBPayrollDuplicateSource;
        })
    );

    return sources.filter(Boolean) as RBPayrollDuplicateSource[];
  },

  // TODO(BE): Admin Approval Workflow
  // TODO(BE): Admin Rejection Workflow
};
